import React, { useState, useEffect } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useNavigate } from "react-router-dom";
import Button from "@shared/components/Button";
import { evaluationService } from "@services/EvaluationService";
import type {
  ReportEvaluationRunSchema,
  SingleReportEvaluationSchema,
  SingleReportResults,
} from "@services/EvaluationService";
import SingleReportResultsModal from "./components/SingleReportResults";
import { toastService } from "@services/ToastService";

interface EvaluationRunData {
  dataset_name: string;
  run_name?: string;
  run_description?: string;
  regenerate_outputs: boolean;
}

type EvaluationType = "assistant" | "report";
type ReportEvaluationType = "langfuse_dataset" | "single";

const Evaluation: React.FC = () => {
  const navigate = useNavigate();
  const { evaluation: evaluationFlag } = useFlags();

  useEffect(() => {
    if (!evaluationFlag) {
      navigate("/assistant");
    }
  }, [evaluationFlag, navigate]);

  // Main tab state
  const [activeTab, setActiveTab] = useState<EvaluationType>("assistant");

  // Assistant evaluation states
  const [datasetName, setDatasetName] = useState<string>("");
  const [runName, setRunName] = useState<string>("");
  const [runDescription, setRunDescription] = useState<string>("");
  const [regenerateOutputs, setRegenerateOutputs] = useState<boolean>(false);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Report evaluation states
  const [reportEvalType, setReportEvalType] =
    useState<ReportEvaluationType>("langfuse_dataset");
  const [reportDatasetName, setReportDatasetName] = useState<string>("");
  const [reportRunName, setReportRunName] = useState<string>("");
  const [reportRunDescription, setReportRunDescription] = useState<string>("");
  const [reportSelectedFile, setReportSelectedFile] = useState<File | null>(
    null
  );
  const [isReportRunning, setIsReportRunning] = useState<boolean>(false);

  // Single report evaluation states
  const [aiGeneratedPdf, setAiGeneratedPdf] = useState<File | null>(null);
  const [referencePdf, setReferencePdf] = useState<File | null>(null);
  const [reportId, setReportId] = useState<string>("");
  const [useReportId, setUseReportId] = useState<boolean>(false);
  const [evaluationResults, setEvaluationResults] =
    useState<SingleReportResults | null>(null);
  const [showResults, setShowResults] = useState<boolean>(false);

  const handleRunEvaluation = async () => {
    if (!datasetName.trim()) {
      toastService.error("Dataset name is required");
      return;
    }

    setIsRunning(true);

    try {
      // Step 1: Upload file first if one is selected
      if (selectedFile) {
        try {
          const uploadResponse = await evaluationService.uploadFile(
            datasetName.trim(),
            selectedFile
          );
          toastService.success(
            `File uploaded successfully! Added ${uploadResponse.items_added} items (${uploadResponse.total_rows} rows processed) to dataset '${datasetName}'.`
          );

          // Clear the file after successful upload
          setSelectedFile(null);
          const fileInput = document.getElementById(
            "fileInput"
          ) as HTMLInputElement;
          if (fileInput) fileInput.value = "";
        } catch (uploadError) {
          console.error("Error uploading file:", uploadError);
          toastService.error("Failed to upload file. Experiment cancelled.");
          return;
        }
      }

      // Step 2: Run the evaluation experiment
      const runData: EvaluationRunData = {
        dataset_name: datasetName.trim(),
        run_name: runName.trim() || undefined,
        run_description: runDescription.trim() || undefined,
        regenerate_outputs: regenerateOutputs,
      };

      const response = await evaluationService.runEvaluation(runData);
      toastService.success(
        `Evaluation experiment started successfully! Task ID: ${response.task_id}`
      );
    } catch (error) {
      console.error("Error running evaluation:", error);
      toastService.error("Failed to start evaluation experiment");
    } finally {
      setIsRunning(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type (CSV or XLSX only)
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith(".csv") && !fileName.endsWith(".xlsx")) {
        toastService.error("Please select a CSV or XLSX file");
        return;
      }

      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toastService.error("File size too large. Maximum allowed size is 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleReportFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type (CSV or XLSX only)
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith(".csv") && !fileName.endsWith(".xlsx")) {
        toastService.error("Please select a CSV or XLSX file");
        return;
      }

      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toastService.error("File size too large. Maximum allowed size is 10MB");
        return;
      }
      setReportSelectedFile(file);
    }
  };

  const handlePdfFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "ai" | "reference"
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type (PDF only)
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith(".pdf")) {
        toastService.error("Please select a PDF file");
        return;
      }

      // Check file size (30MB limit for PDFs)
      if (file.size > 30 * 1024 * 1024) {
        toastService.error("File size too large. Maximum allowed size is 30MB");
        return;
      }

      if (type === "ai") {
        setAiGeneratedPdf(file);
      } else {
        setReferencePdf(file);
      }
    }
  };

  const handleRunReportEvaluation = async () => {
    if (reportEvalType === "langfuse_dataset") {
      if (!reportDatasetName.trim()) {
        toastService.error("Dataset name is required");
        return;
      }

      setIsReportRunning(true);

      try {
        // Step 1: Upload file first if one is selected
        if (reportSelectedFile) {
          try {
            const uploadResponse = await evaluationService.uploadReportFile(
              reportDatasetName.trim(),
              reportSelectedFile
            );
            toastService.success(
              `File uploaded successfully! Added ${uploadResponse.items_added} items (${uploadResponse.total_rows} rows processed) to dataset '${reportDatasetName}'.`
            );

            // Clear the file after successful upload
            setReportSelectedFile(null);
            const fileInput = document.getElementById(
              "reportFileInput"
            ) as HTMLInputElement;
            if (fileInput) fileInput.value = "";
          } catch (uploadError) {
            console.error("Error uploading file:", uploadError);
            toastService.error("Failed to upload file. Experiment cancelled.");
            return;
          }
        }

        // Step 2: Run the report evaluation experiment (without regenerate_outputs)
        // Use the new report evaluation method
        const reportRunData: ReportEvaluationRunSchema = {
          dataset_name: reportDatasetName.trim(),
          run_name: reportRunName.trim() || undefined,
          run_description: reportRunDescription.trim() || undefined,
        };

        const response = await evaluationService.runReportEvaluation(
          reportRunData
        );
        toastService.success(
          `Report evaluation experiment started successfully! Task ID: ${response.task_id}`
        );
      } catch (error) {
        console.error("Error running report evaluation:", error);
        toastService.error("Failed to start report evaluation experiment");
      } finally {
        setIsReportRunning(false);
      }
    } else {
      // Single report evaluation
      if (useReportId) {
        if (!reportId.trim() || !referencePdf) {
          toastService.error("Report ID and reference PDF are required");
          return;
        }
      } else {
        if (!aiGeneratedPdf || !referencePdf) {
          toastService.error(
            "Both AI-generated and reference PDFs are required"
          );
          return;
        }
      }

      setIsReportRunning(true);

      try {
        const singleEvalData: SingleReportEvaluationSchema = {
          ai_generated_pdf: useReportId ? undefined : aiGeneratedPdf!,
          reference_pdf: referencePdf!,
          report_id: useReportId ? reportId.trim() : undefined,
          use_report_id: useReportId,
        };

        const response = await evaluationService.runSingleReportEvaluation(
          singleEvalData
        );

        // Display results if they are included in the response
        if (response.results) {
          setEvaluationResults(response.results);
          setShowResults(true);
          toastService.success(
            `Single report evaluation completed successfully! Task ID: ${response.task_id}`
          );
        } else {
          toastService.success(
            `Single report evaluation started successfully! Task ID: ${response.task_id}`
          );
        }
      } catch (error) {
        console.error("Error running single report evaluation:", error);
        toastService.error("Failed to start single report evaluation");
      } finally {
        setIsReportRunning(false);
      }
    }
  };

  const renderTabButton = (tab: EvaluationType, label: string) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`px-6 py-3 font-medium text-sm rounded-t-lg transition-colors ${
        activeTab === tab
          ? "bg-white text-blue-600 border-t border-l border-r border-gray-300 -mb-px"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      }`}
    >
      {label}
    </button>
  );

  const renderAssistantEvaluation = () => (
    <div className="space-y-8">
      {/* Dataset Configuration Section */}
      <div>
        <div className="space-y-4">
          <div>
            <label className="block text-lg font-semibold text-gray-700 mb-2">
              Langfuse Dataset Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={datasetName}
              onChange={(e) => setDatasetName(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-3 h-12 focus:outline-none focus:border-blue-500"
              placeholder="Enter dataset name"
              disabled={isRunning}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Experiment Name
              </label>
              <input
                type="text"
                value={runName}
                onChange={(e) => setRunName(e.target.value)}
                className="w-full border border-gray-300 rounded-md p-2 h-10 focus:outline-none focus:border-blue-500"
                placeholder="e.g., baseline_test"
                disabled={isRunning}
              />
              <p className="text-xs text-gray-500 mt-1">
                Auto-generated if empty
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Experiment Description
              </label>
              <input
                type="text"
                value={runDescription}
                onChange={(e) => setRunDescription(e.target.value)}
                className="w-full border border-gray-300 rounded-md p-2 h-10 focus:outline-none focus:border-blue-500"
                placeholder="Brief description (optional)"
                disabled={isRunning}
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={regenerateOutputs}
                  onChange={(e) => setRegenerateOutputs(e.target.checked)}
                  className="appearance-none w-5 h-5 border-2 border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 checked:bg-blue-500 checked:border-blue-500 transition-all duration-200"
                  disabled={isRunning}
                />
                {regenerateOutputs && (
                  <svg
                    className="absolute inset-0 w-5 h-5 text-white pointer-events-none"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <span className="text-gray-800 font-medium">
                Regenerate outputs (ignore existing generated answers in
                dataset)
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* File Attachment Section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Attach Data File{" "}
          <span className="text-gray-500 font-normal">(Optional)</span>
        </h2>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <div className="text-center">
            <div className="mb-4">
              <input
                type="file"
                id="fileInput"
                accept=".csv,.xlsx"
                onChange={handleFileChange}
                className="hidden"
                disabled={isRunning}
              />
              <label
                htmlFor="fileInput"
                className={`cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 ${
                  isRunning ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  />
                </svg>
                {selectedFile ? "Change File" : "Attach File"}
              </label>
            </div>

            {selectedFile && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center justify-center space-x-2">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span className="text-sm font-medium text-blue-800">
                    {selectedFile.name}
                  </span>
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      const fileInput = document.getElementById(
                        "fileInput"
                      ) as HTMLInputElement;
                      if (fileInput) fileInput.value = "";
                    }}
                    className="text-blue-600 hover:text-blue-800"
                    disabled={isRunning}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-blue-600 mt-1">
                  This file will be uploaded to the dataset when you run the
                  experiment
                </p>
              </div>
            )}

            <p className="text-xs text-gray-500">
              Attach data to be added to the Langfuse dataset before running the
              experiment. Accepts CSV or XLSX files. Maximum file size: 10MB.
            </p>
          </div>
        </div>
      </div>

      {/* Run Evaluation Section */}
      <div className="border-t pt-6">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Run Evaluation Experiment
          </h2>
          {selectedFile && (
            <div className="flex items-center space-x-2 text-sm text-blue-600">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>
                Will upload "{selectedFile.name}" and then run the experiment
              </span>
            </div>
          )}
        </div>

        <Button
          onClick={handleRunEvaluation}
          label={
            isRunning
              ? selectedFile
                ? "Uploading file and running experiment..."
                : "Running experiment..."
              : selectedFile
              ? "Upload File & Run Experiment"
              : "Run Evaluation Experiment"
          }
          className={`w-full py-4 rounded-lg font-medium text-lg transition ${
            isRunning || !datasetName.trim()
              ? "bg-gray-400 text-white cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-500"
          }`}
        />

        {!datasetName.trim() && (
          <p className="text-sm text-gray-500 mt-2">
            Please enter a dataset name to run evaluation
          </p>
        )}
      </div>
    </div>
  );

  const renderReportEvaluation = () => (
    <div className="space-y-8">
      {/* Report Evaluation Type Selection */}
      <div>
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Report Evaluation Type
        </h2>
        <div className="flex space-x-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              name="reportEvalType"
              value="langfuse_dataset"
              checked={reportEvalType === "langfuse_dataset"}
              onChange={(e) =>
                setReportEvalType(e.target.value as ReportEvaluationType)
              }
              className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              disabled={isReportRunning}
            />
            <span className="text-gray-700 font-medium">
              Langfuse Dataset Evaluation
            </span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              name="reportEvalType"
              value="single"
              checked={reportEvalType === "single"}
              onChange={(e) =>
                setReportEvalType(e.target.value as ReportEvaluationType)
              }
              className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              disabled={isReportRunning}
            />
            <span className="text-gray-700 font-medium">
              Single Report Evaluation
            </span>
          </label>
        </div>
      </div>

      {reportEvalType === "langfuse_dataset" ? (
        <div className="space-y-6">
          {/* Dataset Configuration Section */}
          <div>
            <div className="space-y-4">
              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-2">
                  Langfuse Dataset Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={reportDatasetName}
                  onChange={(e) => setReportDatasetName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-3 h-12 focus:outline-none focus:border-blue-500"
                  placeholder="Enter dataset name"
                  disabled={isReportRunning}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Experiment Name
                  </label>
                  <input
                    type="text"
                    value={reportRunName}
                    onChange={(e) => setReportRunName(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2 h-10 focus:outline-none focus:border-blue-500"
                    placeholder="e.g., baseline_test"
                    disabled={isReportRunning}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Auto-generated if empty
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Experiment Description
                  </label>
                  <input
                    type="text"
                    value={reportRunDescription}
                    onChange={(e) => setReportRunDescription(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2 h-10 focus:outline-none focus:border-blue-500"
                    placeholder="Brief description (optional)"
                    disabled={isReportRunning}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* File Attachment Section */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Attach Data File{" "}
              <span className="text-gray-500 font-normal">(Optional)</span>
            </h2>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center">
                <div className="mb-4">
                  <input
                    type="file"
                    id="reportFileInput"
                    accept=".csv,.xlsx"
                    onChange={handleReportFileChange}
                    className="hidden"
                    disabled={isReportRunning}
                  />
                  <label
                    htmlFor="reportFileInput"
                    className={`cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 ${
                      isReportRunning ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                      />
                    </svg>
                    {reportSelectedFile ? "Change File" : "Attach File"}
                  </label>
                </div>

                {reportSelectedFile && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center justify-center space-x-2">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="text-sm font-medium text-blue-800">
                        {reportSelectedFile.name}
                      </span>
                      <button
                        onClick={() => {
                          setReportSelectedFile(null);
                          const fileInput = document.getElementById(
                            "reportFileInput"
                          ) as HTMLInputElement;
                          if (fileInput) fileInput.value = "";
                        }}
                        className="text-blue-600 hover:text-blue-800"
                        disabled={isReportRunning}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      This file will be uploaded to the dataset when you run the
                      experiment
                    </p>
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  Attach data to be added to the Langfuse dataset before running
                  the experiment. Accepts CSV or XLSX files. Maximum file size:
                  10MB.
                </p>
              </div>
            </div>
          </div>

          {/* Run Report Evaluation Section */}
          <div className="border-t pt-6">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Run Report Evaluation Experiment
              </h2>
              {reportSelectedFile && (
                <div className="flex items-center space-x-2 text-sm text-blue-600">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    Will upload "{reportSelectedFile.name}" and then run the
                    experiment
                  </span>
                </div>
              )}
            </div>

            <Button
              onClick={handleRunReportEvaluation}
              label={
                isReportRunning
                  ? reportSelectedFile
                    ? "Uploading file and running experiment..."
                    : "Running experiment..."
                  : reportSelectedFile
                  ? "Upload File & Run Experiment"
                  : "Run Report Evaluation Experiment"
              }
              className={`w-full py-4 rounded-lg font-medium text-lg transition ${
                isReportRunning || !reportDatasetName.trim()
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-500"
              }`}
            />

            {!reportDatasetName.trim() && (
              <p className="text-sm text-gray-500 mt-2">
                Please enter a dataset name to run evaluation
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Single Report Evaluation Configuration */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Report Input Method
            </h2>
            <div className="flex space-x-4 mb-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="inputMethod"
                  checked={!useReportId}
                  onChange={() => setUseReportId(false)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  disabled={isReportRunning}
                />
                <span className="text-gray-700 font-medium">
                  Upload Two PDFs
                </span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="inputMethod"
                  checked={useReportId}
                  onChange={() => setUseReportId(true)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  disabled={isReportRunning}
                />
                <span className="text-gray-700 font-medium">
                  Reference PDF + Report ID
                </span>
              </label>
            </div>

            {!useReportId ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* AI Generated PDF Upload */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-2">
                    AI-Generated Report PDF{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <input
                      type="file"
                      id="aiPdfInput"
                      accept=".pdf"
                      onChange={(e) => handlePdfFileChange(e, "ai")}
                      className="hidden"
                      disabled={isReportRunning}
                    />
                    <label
                      htmlFor="aiPdfInput"
                      className={`cursor-pointer block text-center ${
                        isReportRunning ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {aiGeneratedPdf ? (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                          <div className="flex items-center justify-center space-x-2">
                            <svg
                              className="w-5 h-5 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span className="text-sm font-medium text-green-800">
                              {aiGeneratedPdf.name}
                            </span>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                setAiGeneratedPdf(null);
                                const fileInput = document.getElementById(
                                  "aiPdfInput"
                                ) as HTMLInputElement;
                                if (fileInput) fileInput.value = "";
                              }}
                              className="text-green-600 hover:text-green-800 transition-colors"
                              disabled={isReportRunning}
                              title="Remove file"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <svg
                            className="mx-auto h-12 w-12 text-gray-400"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                          >
                            <path
                              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <p className="mt-2 text-sm text-gray-600">
                            Click to upload AI-generated PDF
                          </p>
                          <p className="text-xs text-gray-500">
                            Maximum size: 30MB
                          </p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                {/* Reference PDF Upload */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-2">
                    Reference Report PDF <span className="text-red-600">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <input
                      type="file"
                      id="referencePdfInput"
                      accept=".pdf"
                      onChange={(e) => handlePdfFileChange(e, "reference")}
                      className="hidden"
                      disabled={isReportRunning}
                    />
                    <label
                      htmlFor="referencePdfInput"
                      className={`cursor-pointer block text-center ${
                        isReportRunning ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {referencePdf ? (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                          <div className="flex items-center justify-center space-x-2">
                            <svg
                              className="w-5 h-5 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span className="text-sm font-medium text-green-800">
                              {referencePdf.name}
                            </span>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                setReferencePdf(null);
                                const fileInput = document.getElementById(
                                  "referencePdfInput"
                                ) as HTMLInputElement;
                                if (fileInput) fileInput.value = "";
                              }}
                              className="text-green-600 hover:text-green-800 transition-colors"
                              disabled={isReportRunning}
                              title="Remove file"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <svg
                            className="mx-auto h-12 w-12 text-gray-400"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                          >
                            <path
                              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <p className="mt-2 text-sm text-gray-600">
                            Click to upload reference PDF
                          </p>
                          <p className="text-xs text-gray-500">
                            Maximum size: 30MB
                          </p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Report ID Input */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-2">
                    Report ID <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={reportId}
                    onChange={(e) => setReportId(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-3 h-12 focus:outline-none focus:border-blue-500"
                    placeholder="Enter report ID"
                    disabled={isReportRunning}
                  />
                </div>

                {/* Reference PDF Upload */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-2">
                    Reference Report PDF <span className="text-red-600">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <input
                      type="file"
                      id="referencePdfInput2"
                      accept=".pdf"
                      onChange={(e) => handlePdfFileChange(e, "reference")}
                      className="hidden"
                      disabled={isReportRunning}
                    />
                    <label
                      htmlFor="referencePdfInput2"
                      className={`cursor-pointer block text-center ${
                        isReportRunning ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {referencePdf ? (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                          <div className="flex items-center justify-center space-x-2">
                            <svg
                              className="w-5 h-5 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span className="text-sm font-medium text-green-800">
                              {referencePdf.name}
                            </span>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                setReferencePdf(null);
                                const fileInput = document.getElementById(
                                  "referencePdfInput"
                                ) as HTMLInputElement;
                                if (fileInput) fileInput.value = "";
                              }}
                              className="text-green-600 hover:text-green-800 transition-colors"
                              disabled={isReportRunning}
                              title="Remove file"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <svg
                            className="mx-auto h-12 w-12 text-gray-400"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                          >
                            <path
                              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <p className="mt-2 text-sm text-gray-600">
                            Click to upload reference PDF
                          </p>
                          <p className="text-xs text-gray-500">
                            Maximum size: 30MB
                          </p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Run Single Report Evaluation Section */}
          <div className="border-t pt-6">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Run Single Report Evaluation
              </h2>
            </div>

            <Button
              onClick={handleRunReportEvaluation}
              label={
                isReportRunning
                  ? "Running evaluation..."
                  : "Run Single Report Evaluation"
              }
              className={`w-full py-4 rounded-lg font-medium text-lg transition ${
                isReportRunning ||
                (useReportId
                  ? !reportId.trim() || !referencePdf
                  : !aiGeneratedPdf || !referencePdf)
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-500"
              }`}
            />

            {(useReportId
              ? !reportId.trim() || !referencePdf
              : !aiGeneratedPdf || !referencePdf) && (
              <p className="text-sm text-gray-500 mt-2">
                {useReportId
                  ? "Please provide both Report ID and Reference PDF"
                  : "Please upload both AI-generated and Reference PDFs"}
              </p>
            )}

            {/* View Previous Results Button */}
            {evaluationResults && !showResults && (
              <div className="mt-4">
                <button
                  onClick={() => setShowResults(true)}
                  className="w-full py-2 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors border border-gray-300"
                  disabled={isReportRunning}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>View Previous Results</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-8 pb-0">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">
              Evaluation Pipeline
            </h1>

            {/* Tab Navigation */}
            <div className="flex space-x-1 border-b border-gray-300 mb-8">
              {renderTabButton("assistant", "Assistant Evaluation")}
              {renderTabButton("report", "Report Evaluation")}
            </div>
          </div>

          <div className="p-8 pt-0">
            {/* Tab Content */}
            {activeTab === "assistant" && renderAssistantEvaluation()}
            {activeTab === "report" && renderReportEvaluation()}
          </div>
        </div>
      </div>

      {/* Results Modal */}
      {showResults && evaluationResults && (
        <SingleReportResultsModal
          results={evaluationResults}
          onClose={() => {
            setShowResults(false);
            // Don't clear evaluationResults - keep them for re-viewing
          }}
        />
      )}
    </div>
  );
};

export default Evaluation;
